import type { UkCircleSite } from "./extraction";

export interface ExtractedFields {
  patient_name?: string;
  date_of_birth?: string;
  nhs_number?: string;
  email?: string;
  phone?: string;
  phone_numbers?: string[];
  address?: string;
  site?: UkCircleSite;
  referral_date?: string;
  urgency?: string;
  pathway_ids?: string;
  body_parts?: string[];
  invited_joint?: string;
  gp_name?: string;
  gp_address?: string;
}

export type FieldName = keyof ExtractedFields;

export interface ValidationIssue {
  field: FieldName | "contact" | "file";
  type: "missing" | "mismatch" | "error";
  message: string;
}

export interface ReferralItem {
  id: string;
  file: File;
  status: "pending" | "extracting" | "extracted" | "submitted";
  extracted?: ExtractedFields;
  validationIssues?: ValidationIssue[];
  rawText?: string;
}

export type BatchState =
  | { step: "empty" }
  | { step: "processing"; items: ReferralItem[] }
  | { step: "review"; items: ReferralItem[]; currentIndex: number }
  | { step: "summary"; items: ReferralItem[] }
  | { step: "submitting"; items: ReferralItem[]; currentIndex: number; submittedCount: number; totalToSubmit: number }
  | { step: "success"; submitted: number; failed: number };

export type BatchAction =
  | { type: "ADD_FILES"; files: File[] }
  | { type: "EXTRACTION_START"; id: string }
  | { type: "EXTRACTION_COMPLETE"; id: string; extracted: ExtractedFields; rawText: string; validationIssues: ValidationIssue[] }
  | { type: "UPDATE_FIELDS"; id: string; fields: Partial<ExtractedFields> }
  | { type: "REMOVE_ITEM"; id: string }
  | { type: "START_REVIEW" }
  | { type: "GO_TO_ITEM"; index: number }
  | { type: "NEXT_ITEM" }
  | { type: "PREV_ITEM" }
  | { type: "GO_TO_SUMMARY" }
  | { type: "START_SUBMIT" }
  | { type: "SUBMIT_SUCCESS"; id: string }
  | { type: "SUBMIT_ERROR"; id: string; error: string }
  | { type: "FINISH" }
  | { type: "RESET" };

export function computeValidationIssues(fields?: ExtractedFields): ValidationIssue[] {
  if (!fields) return [{ field: "patient_name", type: "missing", message: "No data extracted" }];

  const issues: ValidationIssue[] = [];
  if (!fields.patient_name) issues.push({ field: "patient_name", type: "missing", message: "Patient Name is required" });
  if (!fields.nhs_number) issues.push({ field: "nhs_number", type: "missing", message: "NHS Number is required" });
  if (!fields.site) issues.push({ field: "site", type: "missing", message: "Site is required" });
  if (!fields.pathway_ids) issues.push({ field: "pathway_ids", type: "missing", message: "Pathway ID is required" });
  if (!fields.body_parts || fields.body_parts.length === 0) issues.push({ field: "body_parts", type: "missing", message: "Body Parts is required" });
  if (!fields.invited_joint) issues.push({ field: "invited_joint", type: "missing", message: "Invited Joint is required" });
  if (!fields.email && !fields.phone) issues.push({ field: "contact", type: "missing", message: "Email or Phone is required" });
  return issues;
}
